/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivateAccountComponent } from './pages/activateAccount/activateAccount.component';
import { ActivateAccountModule } from './pages/activateAccount/activateAccount.module';
import { ActivateMarketingComponent } from './pages/activate-marketing/activate-marketing.component';
import { ActivateMarketingModule } from './pages/activate-marketing/activate-marketing.module';
import { AppComponent } from './app.component';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { CentralisedManagementModule } from './modules/centralisedManagement/centralisedManagement.module';
import { DeepThoughtModule } from './modules/deep-thought/deep-thought.module';
import { DictionaryService, NavigationService } from './core/services';
import { ErrorComponent } from './pages/error/error.component';
import { ErrorHandler, ModuleWithProviders, NgModule } from '@angular/core';
import { ErrorModule } from './pages/error/error.module';
import { ExternalPageWrapperModule } from './pages/externalPageWrapper/external-page-wrapper.module';
import { ExternalPortalOnlyGuard, LoginGuard } from './core/guards';
import { ExternalUtilsService } from './pages/shared/external-utils-service';
import { FreeTrialComponent } from './pages/free-trial/free-trial.component';
import { FreeTrialModule } from './pages/free-trial/free-trial.module';
import { GlobalErrorHandler, MyRouterErrorHandler, PreventErrorRouteReuseStrategy } from './globalErrorHandler';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LoadbalancerModule } from './modules/loadbalancer/loadbalancer.module';
import { LoginComponent } from './pages/login/login.component';
import { LoginModule } from './pages/login/login.module';
import { PasswordRecoveryComponent } from './pages/password-recovery/password-recovery.component';
import { PasswordRecoveryModule } from './pages/password-recovery/password-recovery.module';
import { QuillModule } from 'ngx-quill';
import { RegistrationComponent } from './pages/registration/registration.component';
import { RegistrationModule } from './pages/registration/registration.module';
import { RestartComponent } from './pages/restart/restart.component';
import { RestartModule } from './pages/restart/restart.module';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { SharedModule } from './core/shared.module';
import { ShutdownComponent } from './pages/shutdown/shutdown.component';
import { ShutdownModule } from './pages/shutdown/shutdown.module';
import { Warden } from './warden';

/**
 *
 * @type {ModuleWithProviders}
 */
const RootRouting: ModuleWithProviders<any> = RouterModule.forRoot(
    [
        {
            path: 'login',
            component: LoginComponent,
            canActivate: [LoginGuard]
        },
        {
            path: 'restart',
            component: RestartComponent
        },
        {
            path: 'shutdown',
            component: ShutdownComponent
        },
        {
            path: 'register',
            component: RegistrationComponent,
            canActivate: [LoginGuard, ExternalPortalOnlyGuard]
        },
        {
            path: 'activate/:accountId/:activationCode',
            component: ActivateAccountComponent,
            canActivate: [LoginGuard, ExternalPortalOnlyGuard]
        },
        {
            path: 'activate/:accountId/:activationCode/:invited',
            component: ActivateAccountComponent,
            canActivate: [LoginGuard, ExternalPortalOnlyGuard]
        },
        {
            path: 'activate-portal/:hubspotId',
            component: ActivateMarketingComponent,
            canActivate: [LoginGuard, ExternalPortalOnlyGuard]
        },
        {
            path: 'free-trial/:hubspotId',
            component: FreeTrialComponent,
            canActivate: [LoginGuard, ExternalPortalOnlyGuard]
        },
        {
            path: 'free-trial',
            component: FreeTrialComponent,
            canActivate: [LoginGuard, ExternalPortalOnlyGuard]
        },
        {
            path: 'password-recovery',
            component: PasswordRecoveryComponent,
            canActivate: [LoginGuard, ExternalPortalOnlyGuard]
        },
        {
            path: '400',
            component: ErrorComponent,
        },
        {
            path: '403',
            component: ErrorComponent,
        },
        {
            path: '404',
            component: ErrorComponent
        },
        {
            path: '**',
            redirectTo: '/login',
            pathMatch: 'full'
        }
    ],
    {
        errorHandler: MyRouterErrorHandler,
        enableTracing: false
    }
);

/**
 *
 */
@NgModule({
    declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent],
    imports: [
        // Pages
        ActivateAccountModule,
        ActivateMarketingModule,
        BrowserAnimationsModule,
        BrowserModule,
        CentralisedManagementModule,
        DeepThoughtModule,
        ErrorModule,
        ExternalPageWrapperModule,
        FreeTrialModule,
        LoadbalancerModule,
        LoginModule,
        PasswordRecoveryModule,
        QuillModule.forRoot(),
        RegistrationModule,
        RestartModule,
        RootRouting,
        SharedModule,
        ShutdownModule
    ],
    providers: [
        DictionaryService,
        ExternalUtilsService,
        NavigationService,
        Warden,
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        {
            provide: RouteReuseStrategy,
            useClass: PreventErrorRouteReuseStrategy
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        provideHttpClient(withInterceptorsFromDi()),
    ]
})
export class AppModule {
}
